import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SoundTherapyStep } from './SoundTherapyStep';
import { BreathingStep } from './BreathingStep';
import { BeliefDetoxStep } from './BeliefDetoxStep';
import { Sparkles } from 'lucide-react';
import { useClickSound } from '../../hooks/useClickSound';
import { TherapyConfig } from '../EmotionScanner';
interface DailyRitualProps {
  onComplete: (pointsEarned: number) => void;
  onClose?: () => void;
  therapyConfig?: TherapyConfig;
}
export function DailyRitual({
  onComplete,
  onClose,
  therapyConfig
}: DailyRitualProps) {
  const [step, setStep] = useState(1);
  const {
    playSuccess,
    playClick
  } = useClickSound();
  // Use therapy config or defaults
  const soundHz = therapyConfig?.sound_hz ?? 432;
  const soundName = therapyConfig?.sound_name ?? '432 Hz — Częstotliwość Ziemi';
  const ritualDescription = therapyConfig?.ritual_description ?? 'Terapia dźwiękiem przywracająca naturalny przepływ energii.';
  const breathingTechnique = therapyConfig?.breathing_technique ?? 'box';
  const breathingName = therapyConfig?.breathing_name ?? 'Oddech Pudełkowy';
  const breathingPattern = therapyConfig?.breathing_pattern ?? [4, 4, 4, 4];
  const ritualTitle = therapyConfig?.ritual_title ?? 'Rytuał Harmonii';
  const handleStepComplete = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      setStep(4);
      playSuccess();
    }
  };
  const handleFinish = () => {
    playClick();
    onComplete(10);
  };
  // Map sound_hz to a color
  const getSoundColor = () => {
    if (soundHz <= 432) return '16, 185, 129'; // emerald
    if (soundHz <= 528) return '99, 102, 241'; // indigo
    if (soundHz <= 852) return '168, 85, 247'; // purple
    return '14, 165, 233'; // cyan
  };
  return <AnimatePresence mode="wait" data-id="element-812">
      {step === 1 && <SoundTherapyStep key="step1" frequency={soundHz} colorRGB={getSoundColor()} duration={30} onComplete={handleStepComplete} onClose={onClose} soundName={soundName} soundDesc={ritualDescription} data-id="element-813" />}
      {step === 2 && <BreathingStep key="step2" onComplete={handleStepComplete} onClose={onClose} breathingTechnique={breathingTechnique} breathingName={breathingName} breathingPattern={breathingPattern} data-id="element-814" />}
      {step === 3 && <BeliefDetoxStep key="step3" onComplete={handleStepComplete} onClose={onClose} data-id="element-815" />}
      {step === 4 && <motion.div key="summary" className="fixed inset-0 z-50 flex flex-col items-center justify-center w-full h-full bg-zen-bg text-zen-text p-6" initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0
    }} data-id="element-816">
          <div className="w-24 h-24 bg-yellow-50 rounded-full flex items-center justify-center mb-6 border-4 border-yellow-100 shadow-xl" data-id="element-817">
            <Sparkles className="w-10 h-10 text-yellow-500" fill="currentColor" data-id="element-818" />
          </div>
          <h2 className="text-3xl font-light mb-2 text-center" data-id="element-819">
            {ritualTitle} Zakończony
          </h2>
          <p className="text-slate-500 text-center mb-8 max-w-xs" data-id="element-820">
            Twoje ciało, energia i umysł są teraz w pełnej harmonii.
          </p>

          <div className="flex space-x-4 mb-12" data-id="element-821">
            <div className="clean-panel rounded-2xl p-4 flex flex-col items-center min-w-[100px]" data-id="element-822">
              <span className="text-xs text-slate-400 uppercase tracking-widest mb-1" data-id="element-823">
                Energia
              </span>
              <span className="text-xl font-bold text-yellow-600 flex items-center" data-id="element-824">
                +10 <Sparkles className="w-4 h-4 ml-1" fill="currentColor" data-id="element-825" />
              </span>
            </div>
            <div className="clean-panel rounded-2xl p-4 flex flex-col items-center min-w-[100px]" data-id="element-826">
              <span className="text-xs text-slate-400 uppercase tracking-widest mb-1" data-id="element-827">
                Dni z rzędu
              </span>
              <span className="text-xl font-bold text-zen-accent flex items-center" data-id="element-828">
                3 🔥
              </span>
            </div>
          </div>

          <button onClick={handleFinish} className="w-full max-w-xs bg-zen-accent text-white rounded-full py-4 text-sm font-bold hover:bg-zen-accent/90 transition-colors shadow-lg" data-id="element-829">
            Wróć do kokpitu
          </button>
        </motion.div>}
    </AnimatePresence>;
}